import { Fragment, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import {
  fetchIncidents, fetchIncidentFacets, deactivateIncidentFn,
  deactivateIncidentsByReasonFn, purgeInactiveIncidentsFn,
} from '@/api/endpoints';
import type { Incident, IncidentListQuery } from '@/api/types';

const PAGE_SIZE = 50;

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
}

function levelBadge(level: string): string {
  const l = level.toUpperCase();
  if (l === 'ERROR' || l === 'CRITICAL') return 'badge-slate';
  if (l === 'WARN' || l === 'WARNING') return 'badge-amber';
  return 'badge-blue';
}

export default function Incidents({ device }: { device: Device }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superAdmin';
  const toast = useToast();
  const qc = useQueryClient();

  const [activeFilter, setActiveFilter] = useState<'true' | 'all'>('true');
  const [level, setLevel] = useState<string>('');   // '' = all
  const [source, setSource] = useState<string>('');  // '' = all
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Incident | null>(null);

  const query: IncidentListQuery = {
    active: activeFilter,
    level: level || undefined,
    source: source || undefined,
    q: search.trim() || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    sort: 'date_utc',
    order: 'desc',
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['incidents', query],
    queryFn: () => fetchIncidents(query),
  });
  const { data: facets } = useQuery({ queryKey: ['incidents', 'facets'], queryFn: fetchIncidentFacets });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['incidents'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const deactivateMut = useConfirmedMutation(
    (id: number) => deactivateIncidentFn(id),
    {
      title: 'Désactiver l’incident',
      message: 'L’incident sera marqué inactif (active = false). Continuer ?',
      onSuccess: () => { toast.push('Incident désactivé', 'success'); setSelected(null); refresh(); },
      onError: (e) => { if (e.message !== 'ConfirmRejected') toast.push('Échec de la désactivation', 'error'); },
    },
  );
  const byReasonMut = useConfirmedMutation(
    (reason: string) => deactivateIncidentsByReasonFn(reason),
    {
      title: 'Désactiver par reason',
      message: 'Tous les incidents ACTIFS partageant cette reason seront désactivés. Continuer ?',
      onSuccess: (r) => { toast.push(`${r.affected} incident(s) désactivé(s)`, 'success'); setSelected(null); refresh(); },
      onError: (e) => { if (e.message !== 'ConfirmRejected') toast.push('Échec de la désactivation groupée', 'error'); },
    },
  );
  const purgeMut = useConfirmedMutation(
    (_: void) => purgeInactiveIncidentsFn(),
    {
      title: 'Purge des incidents inactifs',
      message: 'Supprime DÉFINITIVEMENT tous les incidents inactifs. Action irréversible.',
      destructive: true,
      onSuccess: (r) => { toast.push(`${r.purged} incident(s) purgé(s)`, 'success'); refresh(); },
      onError: (e) => { if (e.message !== 'ConfirmRejected') toast.push('Échec de la purge', 'error'); },
    },
  );

  const busy = deactivateMut.isPending || byReasonMut.isPending || purgeMut.isPending;

  return (
    <PageShell device={device} title="Incidents" breadcrumb="Services">
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex h-9 rounded-md border border-input bg-background overflow-hidden" role="group" aria-label="Filtrer actifs">
            <button onClick={() => { setActiveFilter('true'); setPage(0); }} className={`px-3 text-xs font-medium ${activeFilter === 'true' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Actifs</button>
            <button onClick={() => { setActiveFilter('all'); setPage(0); }} className={`px-3 text-xs font-medium border-l border-border ${activeFilter === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Tous</button>
          </div>

          <select value={level} onChange={(e) => { setLevel(e.target.value); setPage(0); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm">
            <option value="">level — tous</option>
            {(facets?.levels ?? []).map((l) => <option key={l} value={l}>{l}</option>)}
          </select>

          <select value={source} onChange={(e) => { setSource(e.target.value); setPage(0); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm max-w-[180px]">
            <option value="">source — toutes</option>
            {(facets?.sources ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <input
            type="search"
            placeholder="Rechercher reason / source…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm flex-1 min-w-[180px] max-w-xs"
          />

          <span className="text-xs text-muted-foreground ml-auto">
            {isLoading ? 'Chargement…' : `${total} incident(s)`}
          </span>
          <button
            onClick={() => purgeMut.mutate()}
            disabled={!isSuperAdmin || busy}
            title={isSuperAdmin ? 'Supprimer tous les inactifs' : 'Réservé au superAdmin'}
            className="h-9 px-3 rounded-md text-sm font-medium border border-destructive text-destructive disabled:opacity-40"
          >
            Purge inactifs {isSuperAdmin ? '' : '🔒'}
          </button>
        </div>

        {isError ? (
          <div className="p-8 text-center">
            <p className="text-destructive font-medium">Erreur de chargement</p>
            <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5 w-16">id</th>
                  <th className="text-left px-4 py-2.5">date (UTC)</th>
                  <th className="text-left px-4 py-2.5">source</th>
                  <th className="text-left px-4 py-2.5">level</th>
                  <th className="text-left px-4 py-2.5">reason</th>
                  <th className="text-left px-4 py-2.5 w-20">état</th>
                  <th className="w-24 px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.id} className={`cursor-pointer hover:bg-accent/30 select-none ${r.active ? '' : 'opacity-50'}`} onClick={() => setSelected(r)} title="Cliquer pour détail">
                    <td className="px-4 py-2.5 font-mono text-xs">{r.id}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono whitespace-nowrap">{fmtDate(r.date_utc)}</td>
                    <td className="px-4 py-2.5 text-xs font-mono">{r.source}</td>
                    <td className="px-4 py-2.5"><span className={`${levelBadge(r.level)} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{r.level}</span></td>
                    <td className="px-4 py-2.5 text-xs truncate max-w-md">{r.reason}</td>
                    <td className="px-4 py-2.5">
                      {r.active
                        ? <span className="badge-green px-1.5 py-0.5 rounded text-[10px] font-medium">actif</span>
                        : <span className="badge-slate px-1.5 py-0.5 rounded text-[10px] font-medium">inactif</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {r.active && (
                        <button
                          className="text-xs text-primary hover:underline disabled:opacity-40"
                          disabled={busy}
                          onClick={(e) => { e.stopPropagation(); deactivateMut.mutate(r.id); }}
                        >
                          Désactiver
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!isLoading && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">Aucun incident dans le filtre.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">←</button>
            <span className="text-xs text-muted-foreground">page {page + 1} / {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">→</button>
          </div>
        )}
      </div>

      {selected && (
        <IncidentDetailSheet
          incident={selected}
          busy={busy}
          onClose={() => setSelected(null)}
          onDeactivate={() => deactivateMut.mutate(selected.id)}
          onDeactivateByReason={() => byReasonMut.mutate(selected.reason)}
        />
      )}
    </PageShell>
  );
}

function IncidentDetailSheet({
  incident, busy, onClose, onDeactivate, onDeactivateByReason,
}: {
  incident: Incident;
  busy: boolean;
  onClose: () => void;
  onDeactivate: () => void;
  onDeactivateByReason: () => void;
}) {
  const d = incident.details ?? {};
  const entries = useMemo(() => Object.entries(d), [d]);
  const raw = JSON.stringify(incident, null, 2);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full md:w-[720px] bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-3 flex items-start justify-between z-10">
          <div>
            <div className="text-xs text-muted-foreground">Incident #{incident.id} · <span className="font-mono">{incident.source}</span></div>
            <h2 className="text-lg font-bold flex items-center gap-2 mt-0.5">
              <span className={`${levelBadge(incident.level)} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{incident.level}</span>
              {incident.reason}
            </h2>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">
              {fmtDate(incident.date_utc)} ·{' '}
              {incident.active
                ? <span className="text-green-600 dark:text-green-400">actif</span>
                : <span>inactif</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted" aria-label="Fermer">✕</button>
        </div>

        <div className="p-5 space-y-4 flex-1">
          {entries.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Détails</h3>
              <dl className="text-sm grid grid-cols-[160px_1fr] gap-x-3 gap-y-1.5 bg-muted/30 rounded-md p-3">
                {entries.map(([k, v]) => (
                  <Fragment key={k}>
                    <dt className="text-muted-foreground text-xs uppercase font-mono break-all">{k}</dt>
                    <dd className="font-mono text-xs break-all whitespace-pre-wrap">
                      {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            </section>
          )}

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">JSON brut</h3>
            <pre className="bg-muted/30 font-mono text-[11px] p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all border border-border">{raw}</pre>
          </section>
        </div>

        <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border px-5 py-3 flex gap-2 justify-between">
          <button onClick={onClose} className="h-9 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium">Fermer</button>
          {incident.active && (
            <div className="flex gap-2">
              <button onClick={onDeactivate} disabled={busy} className="h-9 px-4 rounded-md border border-border text-sm font-medium hover:bg-accent disabled:opacity-40">Désactiver</button>
              <button onClick={onDeactivateByReason} disabled={busy} className="h-9 px-4 rounded-md border border-border text-sm font-medium hover:bg-accent disabled:opacity-40">Désactiver par reason</button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
